import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';
import { agencyToday, fmtTime } from '../../utils/timeclock';
import { StatusChip, GeoFlag } from './VisitBits';

// Office view of one day's scheduled visits: missed, late and off-site visits stand out
export const VisitTracker = () => {
  const [day, setDay] = useState(agencyToday());
  const [data, setData] = useState({ visits: [], unscheduled: [] });
  const load = useCallback(() => api.get('/timeclock/visit-tracker', { params: { date: day } }).then((r) => setData(r.data)).catch(() => toast.error('Could not load visits')), [day]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const count = (s) => data.visits.filter((v) => v.status === s).length;
  const offsite = data.visits.filter((v) => v.entry?.inGeo?.offsite).length + data.unscheduled.filter((e) => e.inGeo?.offsite).length;
  const pills = [['Missed', 'bg-red-600'], ['Late', 'bg-orange-500'], ['Not started', 'bg-amber-500'], ['In progress', 'bg-blue-600'], ['Completed', 'bg-green-600']];
  return (
    <div className="space-y-3" data-testid="visit-tracker">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="visit-tracker-date" />
        {pills.map(([s, c]) => <span key={s} className={`text-xs text-white px-2 py-1 rounded-full ${c}`} data-testid={`tracker-count-${s.replace(' ', '-').toLowerCase()}`}>{s}: {count(s)}</span>)}
        <span className="text-xs text-white px-2 py-1 rounded-full bg-red-800" data-testid="tracker-count-offsite">Off-site: {offsite}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-[#d4af37] text-left"><tr>{['Time', 'Patient', 'Visit', 'Employee', 'Status', 'Clock in', 'Clock out', 'Location'].map((h) => <th key={h} className="px-2 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {data.visits.map((v) => (
              <tr key={v.appointmentId} className={`border-t ${['Missed', 'Late'].includes(v.status) ? 'bg-red-50' : ''}`} data-testid={`tracker-row-${v.appointmentId}`}>
                <td className="px-2 py-1.5 whitespace-nowrap font-semibold">{v.time}</td><td className="px-2">{v.patientName}</td><td className="px-2">{v.type}</td>
                <td className="px-2">{v.clinician || v.entry?.staffName}</td><td className="px-2"><StatusChip status={v.status} /></td>
                <td className="px-2 whitespace-nowrap">{v.entry ? fmtTime(v.entry.clockIn) : ''}</td><td className="px-2 whitespace-nowrap">{v.entry?.clockOut ? fmtTime(v.entry.clockOut) : ''}</td>
                <td className="px-2 text-xs">{v.entry && <GeoFlag geo={v.entry.inGeo} compact />}</td>
              </tr>
            ))}
            {!data.visits.length && <tr><td colSpan={8} className="px-2 py-4 text-gray-500" data-testid="visit-tracker-empty">No visits scheduled on this day.</td></tr>}
          </tbody>
        </table>
      </div>
      {data.unscheduled.length > 0 && (
        <div data-testid="tracker-unscheduled">
          <p className="text-sm font-semibold">Visits clocked without a scheduled visit</p>
          <ul className="text-sm divide-y border rounded">
            {data.unscheduled.map((e) => <li key={e.id} className="px-2 py-1.5">{fmtTime(e.clockIn)}–{e.clockOut ? fmtTime(e.clockOut) : 'open'} · {e.staffName} · {e.visitType} – {e.patientName} <GeoFlag geo={e.inGeo} compact /></li>)}
          </ul>
        </div>
      )}
      <p className="text-xs text-gray-500">Not started = no clock-in 5 minutes after the scheduled time (the office is emailed). Late = no clock-in 2 hours after. Missed = no clock-in by the end of the day. Off-site = clock-in more than 0.25 miles from the patient's home address.</p>
    </div>
  );
};
